import { useEffect, useRef } from "react";
import { keepAlive, SESSION_EXPIRED_EVENT } from "@/lib/api";

// Signs the user out after `minutes` without mouse, keyboard, touch or scroll activity.
// The last-activity time is shared through localStorage so an active tab keeps the
// other open tabs signed in too. While the user is active the server session is
// extended at most every 30 seconds; background polling does not count as activity.
const STORAGE_KEY = "fyntune:last-activity";
const EVENTS = ["mousemove", "mousedown", "keydown", "wheel", "scroll", "touchstart"];
const PING_MS = 30 * 1000;
const CHECK_MS = 10 * 1000;

const readShared = () => { try { return Number(window.localStorage.getItem(STORAGE_KEY)) || 0; } catch (e) { return 0; } };
const writeShared = (value) => { try { window.localStorage.setItem(STORAGE_KEY, String(value)); } catch (e) { /* storage blocked: this tab still tracks itself */ } };

export default function useIdleLogout(enabled, minutes, onTimeout) {
  const timeoutRef = useRef(onTimeout);
  timeoutRef.current = onTimeout;

  useEffect(() => {
    if (!enabled || !minutes) return undefined;
    const limit = minutes * 60 * 1000;
    let last = Date.now(); let lastPing = last; let lastWrite = 0; let done = false;
    writeShared(last);

    const expire = () => { if (done) return; done = true; timeoutRef.current(); };
    const check = () => {
      if (done) return;
      last = Math.max(last, readShared());
      if (Date.now() - last >= limit) expire();
    };
    const activity = () => {
      if (done) return;
      const t = Date.now();
      // Coming back after sleep: decide on the old timestamp before counting this as activity.
      if (t - Math.max(last, readShared()) >= limit) return expire();
      last = t;
      if (t - lastWrite > 5000) { lastWrite = t; writeShared(t); }
      if (t - lastPing > PING_MS) {
        lastPing = t;
        keepAlive().then((ok) => { if (!ok && !done) window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT)); });
      }
    };
    const visible = () => { if (document.visibilityState === "visible") check(); };

    EVENTS.forEach((name) => window.addEventListener(name, activity, { passive: true }));
    document.addEventListener("visibilitychange", visible);
    const timer = setInterval(check, CHECK_MS);
    return () => {
      done = true;
      clearInterval(timer);
      EVENTS.forEach((name) => window.removeEventListener(name, activity));
      document.removeEventListener("visibilitychange", visible);
    };
  }, [enabled, minutes]);
}
